import type { Timezone } from '@ipfabric/utils/date';
import { type ForwardedRef, forwardRef, useCallback, useMemo } from 'react';
import { type FieldValues, useController } from 'react-hook-form';
import { Cron, type CronRef } from '../Cron/Cron';
import type { CronFormMeta } from '../Cron/cronTypes';
import type {
  FormControlProps,
  TypedUseControllerProps,
} from '../FormControl/FormControl.types';
import { FormField } from '../FormField/FormField';
import type { FormFieldBaseProps } from '../FormField/FormField.types';

export type CronFormValue = {
  expression: string;
  timezone: string;
};

type FormCronFieldProps = FormFieldBaseProps;

type FormCronExternalErrorProp = {
  error?: string;
};

type FormCronMetaCallback = {
  /** Called when cron internal form meta changes (dirty/valid state) */
  onMetaChange?: (meta: CronFormMeta) => void;
};

export type FormCronProps<TFieldValues extends FieldValues = FieldValues> =
  Omit<
    TypedUseControllerProps<TFieldValues, CronFormValue> &
      FormControlProps<FormCronFieldProps> &
      FormCronExternalErrorProp &
      FormCronMetaCallback,
    'htmlFor' | 'link' | 'size'
  >;

const getTimezone = (value: CronFormValue | undefined): Timezone =>
  (value?.timezone ?? 'UTC') as Timezone;

const BareFormCron = <TFieldValues extends FieldValues = FieldValues>(
  {
    className,
    label,
    labelTooltip,
    description,
    primaryHelperText,
    secondaryHelperText,
    error: externalError,
    disabled = false,
    onMetaChange,
    ...props
  }: FormCronProps<TFieldValues>,
  ref: ForwardedRef<CronRef>,
) => {
  const {
    field: { value, onChange },
    fieldState,
  } = useController(props);

  const cronValue = value as CronFormValue | undefined;
  const expression = cronValue?.expression ?? '';
  const timezone = useMemo(() => getTimezone(cronValue), [cronValue]);

  const handleCronChange = useCallback(
    (nextExpression: string, nextTimezone: Timezone, meta: CronFormMeta) => {
      onChange({
        expression: nextExpression,
        timezone: nextTimezone,
      } as CronFormValue);
      onMetaChange?.(meta);
    },
    [onChange, onMetaChange],
  );

  const error = externalError ?? fieldState.error?.message?.toString();

  return (
    <FormField
      className={className}
      label={label}
      labelTooltip={labelTooltip}
      description={description}
      primaryHelperText={primaryHelperText}
      secondaryHelperText={secondaryHelperText}
      error={error}
      disabled={disabled}
    >
      <Cron
        ref={ref}
        initialExpression={expression}
        initialTimezone={timezone}
        onChange={handleCronChange}
        disabled={disabled}
      />
    </FormField>
  );
};

export const FormCron = forwardRef(BareFormCron) as <
  TFieldValues extends FieldValues = FieldValues,
>(
  props: FormCronProps<TFieldValues> & { ref?: ForwardedRef<CronRef> },
) => ReturnType<typeof BareFormCron>;
